import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { Btn, Badge, Avatar, ProgressBar, glass, Select , Checkbox} from '../components/UI';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import supabase from '../data/supabase';

const PRIORITY_COLORS = { urgent: "#E53935", high: "#D4763A", medium: T.blue, low: T.tx3, none: T.tx4 };
const PRIORITY_LABELS = { urgent: "Urgent", high: "High", medium: "Medium", low: "Low", none: "None" };
const COL_COLORS = [T.blue,"#2E8B57","#D4763A","#DC2626","#6B5B95","#3D8B8B","#E53935","#3D8B8B","#D4763A","#4059AD"];

// Trello-style cover colors for cards. Picked to match CoreFlow palette without changing branding.
const COVER_COLORS = [
  { name: "None", value: null },
  { name: "Blue", value: "#4059AD" },
  { name: "Green", value: "#2E8B57" },
  { name: "Orange", value: "#D4763A" },
  { name: "Red", value: "#DC2626" },
  { name: "Purple", value: "#6B5B95" },
  { name: "Teal", value: "#3D8B8B" },
  { name: "Pink", value: "#E91E63" },
  { name: "Yellow", value: "#F59E0B" },
];

// Resolve a card's full assignee list, supporting both new (assignee_ids array) and legacy (assignee_id) shapes.
// Returns array of user IDs, deduped, never null.
function getCardAssignees(card) {
  if (!card) return [];
  const fromArray = Array.isArray(card.assignee_ids) ? card.assignee_ids : [];
  const fromLegacy = card.assignee_id ? [card.assignee_id] : [];
  // Dedupe — assignee_id may already be in array after backfill
  return Array.from(new Set([...fromArray, ...fromLegacy]));
}

const fmtDate = (d) => { if (!d) return ""; const dt = new Date(d + "T00:00:00"); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };
const fmtDateFull = (d) => { if (!d) return ""; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); return m+"/"+dy+"/"+y; };
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const gCard = { ...{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }, padding: 0 };
const gInput = { padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: T.txt, fontSize: 12, outline: "none", fontFamily: "inherit" };
const gBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: "#FAFAFA", fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" };
const gBtnPrimary = { ...gBtn, background: "#4059AD", border: "1px solid #4059AD", color: "#fff" };
const gBtnDanger = { ...gBtn, color: "#DC2626" };

function Modal({ children, onClose, width = 420 }) {
  useEffect(() => { const h = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.50)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 60, overflowY: "auto" }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 40px 100px rgba(0,0,0,0.40)", width, maxWidth: "92vw", padding: 20, maxHeight: "80vh", overflowY: "auto" }}>{children}</div>
  </div>);
}
function EditModal({ title, value, onSave, onClose, onDelete, deleteLabel }) {
  const [v, setV] = useState(value || "");
  const save = () => { if (v.trim()) { onSave(v.trim()); onClose(); }};
  return (<Modal onClose={onClose} width={400}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{title}</span>
      <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.txt, fontSize: 14 }}>×</button>
    </div>
    <input autoFocus value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
      style={{ ...gInput, width: "100%", fontSize: 13 }} />
    <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
      {onDelete && <button onClick={() => { onDelete(); onClose(); }} style={{ ...gBtnDanger, marginRight: "auto", fontSize: 10 }}>{deleteLabel || "Delete"}</button>}
      <button onClick={onClose} style={{ ...gBtn, fontSize: 10 }}>Cancel</button>
      <button onClick={save} style={{ ...gBtnPrimary, fontSize: 10 }}>Save</button>
    </div>
  </Modal>);
}
function ConfirmModal({ title, message, onConfirm, onClose, danger }) {
  return (<Modal onClose={onClose} width={380}>
    <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 8 }}>{title}</div>
    <p style={{ fontSize: 12, color: T.txt, lineHeight: 1.5, margin: "0 0 16px" }}>{message}</p>
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      <button onClick={onClose} style={{ ...gBtn, fontSize: 10 }}>Cancel</button>
      {danger ? <button onClick={() => { onConfirm(); onClose(); }} style={{ ...gBtnDanger, fontSize: 10 }}>Delete</button> : <button onClick={() => { onConfirm(); onClose(); }} style={{ ...gBtnPrimary, fontSize: 10 }}>Confirm</button>}
    </div>
  </Modal>);
}
function QuickModal({ title, onClose, onSubmit, placeholder }) {
  const [val, setVal] = useState("");
  return (<Modal onClose={onClose} width={400}>
    <div style={{ fontSize: 14, fontWeight: 400, color: T.txt, marginBottom: 12 }}>{title}</div>
    <input autoFocus value={val} onChange={e => setVal(e.target.value)} placeholder={placeholder}
      onKeyDown={e => { if (e.key === "Enter" && val.trim()) onSubmit(val.trim()); if (e.key === "Escape") onClose(); }}
      style={{ ...gInput, width: "100%", fontSize: 13 }} />
    <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
      <button onClick={onClose} style={{ ...gBtn, fontSize: 10 }}>Cancel</button>
      <button onClick={() => { if (val.trim()) onSubmit(val.trim()); }} style={{ ...gBtnPrimary, fontSize: 10 }}>Create</button>
    </div>
  </Modal>);
}

// Project edit modal — name + color picker + delete option.
// Used for editing existing projects since EditModal only handles a single text value.
function ProjectEditModal({ title, project, onSave, onClose, onDelete }) {
  const [name, setName] = useState(project?.name || "");
  const [color, setColor] = useState(project?.color || T.blue);
  const PROJECT_COLORS = ["#4059AD", "#2E8B57", "#D4763A", "#DC2626", "#6B5B95", "#3D8B8B", "#E91E63", "#F59E0B", "#7C3AED", "#0EA5E9"];
  const save = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
    onClose();
  };
  return (<Modal onClose={onClose} width={420}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{title}</span>
      <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.txt, fontSize: 14 }}>×</button>
    </div>
    <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, marginBottom: 5, textTransform: "uppercase", letterSpacing: ".06em" }}>Name</div>
    <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
      style={{ ...gInput, width: "100%", fontSize: 13, marginBottom: 14 }} />
    <div style={{ fontSize: 10, fontWeight: 400, color: T.tx3, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".06em" }}>Color</div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
      {PROJECT_COLORS.map(c => {
        const selected = color === c;
        return (
          <button key={c} onClick={() => setColor(c)} title={c}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: c,
              border: selected ? "2px solid #FAFAFA" : "1px solid rgba(217,219,221,0.20)",
              cursor: "pointer", padding: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: selected ? "0 0 0 2px " + c + "55" : "none",
              transition: "all 0.12s",
            }}
          >
            {selected && <span style={{ color: "#fff", fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✓</span>}
          </button>
        );
      })}
    </div>
    <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
      {onDelete && <button onClick={() => { onDelete(); onClose(); }} style={{ ...gBtnDanger, marginRight: "auto", fontSize: 10 }}>Delete Project</button>}
      <button onClick={onClose} style={{ ...gBtn, fontSize: 10 }}>Cancel</button>
      <button onClick={save} style={{ ...gBtnPrimary, fontSize: 10 }}>Save</button>
    </div>
  </Modal>);
}

const Pill = ({ children, color = T.blue, small }) => (
  <span style={{ display: "inline-flex", alignItems: "center", padding: small ? "1px 7px" : "2px 9px",
    borderRadius: 6, fontSize: small ? 9 : 10, fontWeight: 400, background: color + "18", color,
    border: "1px solid " + color + "25", letterSpacing: ".04em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>
);
const PriorityDot = ({ priority, size = 8 }) => priority && priority !== "none" ? <div style={{ width: size, height: size, borderRadius: "50%", background: PRIORITY_COLORS[priority], flexShrink: 0, boxShadow: "0 0 4px " + PRIORITY_COLORS[priority] + "40" }} /> : null;
function SideField({ label, children }) { return (<div style={{ marginBottom: 14 }}><div style={{ fontSize: 10, fontWeight: 400, color: T.txt, marginBottom: 4, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>{children}</div>); }
const sideInput = { width: "100%", padding: "5px 8px", borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", fontSize: 11, outline: "none", color: T.txt, background: "rgba(255,255,255,0.06)" };

// ══════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════
export default function ProjectsPage({ isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const orgId = window.__coreflow_org_id || store.currentUser?._orgId || auth.currentUser?._orgId;
  const userId = store.currentUser?._dbId || auth.currentUser?._dbId;
  const teamMembers = store.teamMembers || [];

  const [projects, setProjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [columns, setColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [fieldValues, setFieldValues] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [selProject, setSelProject] = useState(null);
  const [selBoard, setSelBoard] = useState(null);
  const [view, setView] = useState("kanban");
  const [selCard, setSelCard] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const projectPickerRef = useRef(null);
  const [editModal, setEditModal] = useState(null);
  const [projectEditModal, setProjectEditModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [dragCard, setDragCard] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const loadAll = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      const [pRes, bRes, colRes, cardRes, fdRes, fvRes, stRes, cmRes, autoRes] = await Promise.all([
        supabase.from("projects").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("project_boards").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("board_columns").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("board_cards").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("card_field_defs").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("card_field_values").select("*").eq("org_id", orgId),
        supabase.from("card_subtasks").select("*").eq("org_id", orgId).order("sort_order"),
        supabase.from("card_comments").select("*").eq("org_id", orgId).order("created_at", { ascending: true }),
        supabase.from("board_automations").select("*").eq("org_id", orgId),
      ]);
      [["projects",pRes],["boards",bRes],["columns",colRes],["cards",cardRes],["field_defs",fdRes],["field_values",fvRes],["subtasks",stRes],["comments",cmRes],["automations",autoRes]]
        .forEach(([n, r]) => { if (r.error) console.error("[Projects] " + n + " error:", r.error.message); });
      setProjects(pRes.data || []); setBoards(bRes.data || []); setColumns(colRes.data || []);
      setCards(cardRes.data || []); setFieldDefs(fdRes.data || []); setFieldValues(fvRes.data || []);
      setSubtasks(stRes.data || []); setComments(cmRes.data || []); setAutomations(autoRes.data || []);
    } catch (e) { console.error("[Projects] Load error:", e); }
    setLoading(false);
  }, [orgId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (!selProject && projects.length > 0) setSelProject(projects[0].id); }, [projects, selProject]);
  useEffect(() => { const pb = boards.filter(b => b.project_id === selProject); if (selProject && (!selBoard || !pb.find(b => b.id === selBoard))) setSelBoard(pb[0]?.id || null); }, [selProject, boards, selBoard]);

  // Deep-link: when navigating from notification, auto-open the card
  useEffect(() => {
    const target = window.__coreflow_notification_target;
    if (target && target.type === "board_card" && target.id && cards.length > 0) {
      const card = cards.find(c => c.id === target.id);
      if (card) {
        const board = boards.find(b => b.id === card.board_id);
        if (board) { setSelProject(board.project_id); setSelBoard(board.id); }
        setSelCard(card.id);
      }
      window.__coreflow_notification_target = null;
    }
  }, [cards, boards]);

  const activeProject = projects.find(p => p.id === selProject);
  const projBoards = boards.filter(b => b.project_id === selProject);
  const boardCols = columns.filter(c => c.board_id === selBoard).sort((a, b) => a.sort_order - b.sort_order);
  const boardFieldDefs = fieldDefs.filter(f => f.board_id === selBoard).sort((a, b) => a.sort_order - b.sort_order);
  const boardCards = useMemo(() => {
    let c = cards.filter(c => c.board_id === selBoard && !c.archived_at);
    if (filterAssignee === "unassigned") c = c.filter(x => getCardAssignees(x).length === 0);
    else if (filterAssignee !== "all") c = c.filter(x => getCardAssignees(x).includes(filterAssignee));
    if (filterPriority !== "all") c = c.filter(x => x.priority === filterPriority);
    if (searchText) c = c.filter(x => x.title.toLowerCase().includes(searchText.toLowerCase()));
    return c;
  }, [cards, selBoard, filterAssignee, filterPriority, searchText]);

  const getUser = (id) => teamMembers.find(m => m._dbId === id);
  const getFieldVal = (cardId, defId) => fieldValues.find(v => v.card_id === cardId && v.field_def_id === defId);
  const getSubtasks = (cardId) => subtasks.filter(s => s.card_id === cardId).sort((a, b) => a.sort_order - b.sort_order);
  const getComments = (cardId) => comments.filter(c => c.card_id === cardId);

  // ── CRUD ──
  const createProject = async (name, color) => {
    const { error } = await supabase.from("projects").insert({ org_id: orgId, name, color: color || T.blue, owner_id: userId, sort_order: projects.length });
    if (error) { console.error("[Projects] Create error:", error); return; }
    const { data: np } = await supabase.from("projects").select("*").eq("org_id", orgId).eq("name", name).order("created_at", { ascending: false }).limit(1);
    if (np?.[0]) {
      await supabase.from("project_boards").insert({ org_id: orgId, project_id: np[0].id, name: "Main Board", sort_order: 0 });
      const { data: nb } = await supabase.from("project_boards").select("*").eq("project_id", np[0].id).limit(1);
      if (nb?.[0]) { const dc = ["To Do", "In Progress", "Review", "Done"]; for (let i = 0; i < dc.length; i++) await supabase.from("board_columns").insert({ org_id: orgId, board_id: nb[0].id, name: dc[i], color: COL_COLORS[i], sort_order: i, is_done: i === 3 }); }
      await loadAll(); setSelProject(np[0].id);
    } else { await loadAll(); }
    setShowNewProject(false);
  };
  const createBoard = async (name) => {
    await supabase.from("project_boards").insert({ org_id: orgId, project_id: selProject, name, sort_order: projBoards.length });
    const { data: nb } = await supabase.from("project_boards").select("*").eq("project_id", selProject).eq("name", name).order("created_at", { ascending: false }).limit(1);
    if (nb?.[0]) { const dc = ["To Do", "In Progress", "Done"]; for (let i = 0; i < dc.length; i++) await supabase.from("board_columns").insert({ org_id: orgId, board_id: nb[0].id, name: dc[i], color: COL_COLORS[i], sort_order: i, is_done: i === 2 }); await loadAll(); setSelBoard(nb[0].id); }
    setShowNewBoard(false);
  };
  const createColumn = async (name) => { await supabase.from("board_columns").insert({ org_id: orgId, board_id: selBoard, name, color: COL_COLORS[boardCols.length % COL_COLORS.length], sort_order: boardCols.length }); await loadAll(); };
  const renameColumn = async (id, name) => { await supabase.from("board_columns").update({ name }).eq("id", id); setColumns(prev => prev.map(c => c.id === id ? { ...c, name } : c)); };
  const updateColumn = async (id, updates) => {
    const { error } = await supabase.from("board_columns").update(updates).eq("id", id);
    if (error) { console.error("[Projects] updateColumn error:", error); alert("Could not save: " + error.message); return; }
    setColumns(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };
  // Reorder columns within a board: pass full ordered array of column IDs, persist new sort_order for each.
  const reorderColumns = async (boardId, orderedIds) => {
    // Optimistic update first
    setColumns(prev => prev.map(c => {
      if (c.board_id !== boardId) return c;
      const newIdx = orderedIds.indexOf(c.id);
      return newIdx >= 0 ? { ...c, sort_order: newIdx } : c;
    }));
    // Persist
    const updates = orderedIds.map((id, i) =>
      supabase.from("board_columns").update({ sort_order: i }).eq("id", id)
    );
    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed) {
      console.error("[Projects] reorderColumns error:", failed.error);
      alert("Could not reorder columns: " + failed.error.message);
      await loadAll(); // resync from DB
    }
  };
  const deleteColumn = async (id) => { await supabase.from("board_columns").delete().eq("id", id); await loadAll(); };
  const createCard = async (columnId, title) => {
    const cc = cards.filter(c => c.column_id === columnId);
    const autoAssignee = (filterAssignee && filterAssignee !== "all" && filterAssignee !== "unassigned") ? filterAssignee : null;
    await supabase.from("board_cards").insert({ org_id: orgId, board_id: selBoard, column_id: columnId, title, sort_order: cc.length, created_by: userId, priority: "medium", assignee_id: autoAssignee });
    await loadAll();
  };
  const updateCard = async (id, updates) => {
    const clean = { ...updates };
    ["assignee_id","reporter_id","column_id","contact_id","deal_id","created_by"].forEach(k => { if (k in clean && clean[k] === "") clean[k] = null; });
    ["estimated_hours","actual_hours","sort_order"].forEach(k => { if (k in clean && (clean[k] === "" || clean[k] === undefined || Number.isNaN(clean[k]))) clean[k] = null; });
    ["start_date","due_date"].forEach(k => { if (k in clean && clean[k] === "") clean[k] = null; });
    // If updating assignee_ids, also sync assignee_id (legacy single field) to first element for backwards compat
    if ("assignee_ids" in clean) {
      const arr = Array.isArray(clean.assignee_ids) ? clean.assignee_ids : [];
      clean.assignee_id = arr[0] || null;
    }
    let { error } = await supabase.from("board_cards").update(clean).eq("id", id);

    // Graceful fallback: if migration 037 hasn't been applied yet, the assignee_ids and cover_color columns
    // don't exist. Strip them and retry so the rest of the update still works, and surface a one-time warning.
    if (error && /column .* (assignee_ids|cover_color|label_colors) .* does not exist/i.test(error.message || "")) {
      console.warn("[Projects] Migration 037/038 not applied — falling back to legacy fields");
      const fallback = { ...clean };
      delete fallback.assignee_ids;
      delete fallback.cover_color;
      delete fallback.label_colors;
      const retry = await supabase.from("board_cards").update(fallback).eq("id", id);
      error = retry.error;
      if (!error) {
        // Save succeeded with the legacy-only payload — merge into local state but DON'T merge the dropped fields
        // (otherwise local UI will show stale optimistic data that doesn't match DB).
        setCards(prev => prev.map(c => c.id === id ? { ...c, ...fallback } : c));
        if (!window.__projectsMigrationWarned) {
          window.__projectsMigrationWarned = true;
          alert("Multi-assignee and cover color need a database migration. Run migration 037_card_multi_assignee_and_cover.sql in Supabase to enable these features. Other card edits will still work.");
        }
        return;
      }
    }

    if (error) {
      console.error("[Projects] updateCard error:", error.message, error.details, "payload:", clean);
      alert("Could not save card: " + error.message);
      return;
    }
    setCards(prev => prev.map(c => c.id === id ? { ...c, ...clean } : c));
  };
  const deleteCard = async (id) => { await supabase.from("board_cards").delete().eq("id", id); setCards(prev => prev.filter(c => c.id !== id)); setSelCard(null); };
  const toggleDone = async (id) => { const card = cards.find(c => c.id === id); const now = card?.completed_at ? null : new Date().toISOString(); await supabase.from("board_cards").update({ completed_at: now, updated_at: new Date().toISOString() }).eq("id", id); setCards(prev => prev.map(c => c.id === id ? { ...c, completed_at: now } : c)); };
  const archiveCard = async (id) => { await supabase.from("board_cards").update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", id); setCards(prev => prev.map(c => c.id === id ? { ...c, archived_at: new Date().toISOString() } : c)); setSelCard(null); };
  const moveCard = async (cardId, toColumnId) => { const cc = cards.filter(c => c.column_id === toColumnId); const { error } = await supabase.from("board_cards").update({ column_id: toColumnId, sort_order: cc.length, updated_at: new Date().toISOString() }).eq("id", cardId); if (error) console.error("[Projects] moveCard error:", error); await loadAll(); };

  // Reorder a card to a specific position within a column (or move it to a new column at a specific position).
  // targetIndex is the position in the target column where the card should land (0-based).
  // We rebuild sort_order for ALL cards in both source and target columns to keep things deterministic.
  const reorderCard = async (cardId, toColumnId, targetIndex) => {
    const movingCard = cards.find(c => c.id === cardId);
    if (!movingCard) return;
    const fromColumnId = movingCard.column_id;
    const sameCol = fromColumnId === toColumnId;

    // Build the new ordering for the target column
    const targetCards = cards
      .filter(c => c.column_id === toColumnId && c.id !== cardId && !c.archived_at)
      .sort((a, b) => a.sort_order - b.sort_order);
    const clampedIdx = Math.max(0, Math.min(targetIndex, targetCards.length));
    const newTargetOrder = [
      ...targetCards.slice(0, clampedIdx),
      { ...movingCard, column_id: toColumnId },
      ...targetCards.slice(clampedIdx),
    ];

    // Optimistic local update for instant feedback
    setCards(prev => {
      const next = prev.map(c => {
        if (c.id === cardId) return { ...c, column_id: toColumnId };
        return c;
      });
      // Reapply sort_order for target column
      newTargetOrder.forEach((c, i) => {
        const found = next.find(x => x.id === c.id);
        if (found) found.sort_order = i;
      });
      // If different source column, compact source column sort_orders too
      if (!sameCol) {
        const srcCards = next
          .filter(c => c.column_id === fromColumnId && c.id !== cardId && !c.archived_at)
          .sort((a, b) => a.sort_order - b.sort_order);
        srcCards.forEach((c, i) => { c.sort_order = i; });
      }
      return [...next];
    });

    // Persist sort_order for affected cards. Bulk update — one row at a time but await all.
    const updates = [];
    newTargetOrder.forEach((c, i) => {
      updates.push(
        supabase.from("board_cards").update({ sort_order: i, column_id: toColumnId, updated_at: new Date().toISOString() }).eq("id", c.id)
      );
    });
    if (!sameCol) {
      const srcCards = cards
        .filter(c => c.column_id === fromColumnId && c.id !== cardId && !c.archived_at)
        .sort((a, b) => a.sort_order - b.sort_order);
      srcCards.forEach((c, i) => {
        updates.push(supabase.from("board_cards").update({ sort_order: i, updated_at: new Date().toISOString() }).eq("id", c.id));
      });
    }
    await Promise.all(updates);
  };

  const updateFieldValue = async (cardId, defId, value) => {
    const def = fieldDefs.find(f => f.id === defId); const payload = { org_id: orgId, card_id: cardId, field_def_id: defId };
    if (["number","currency","percent","rating"].includes(def?.field_type)) payload.value_number = value;
    else if (def?.field_type === "date") payload.value_date = value; else if (def?.field_type === "tags") payload.value_json = value; else payload.value_text = value;
    const existing = fieldValues.find(v => v.card_id === cardId && v.field_def_id === defId);
    if (existing) await supabase.from("card_field_values").update(payload).eq("id", existing.id); else await supabase.from("card_field_values").insert(payload); await loadAll();
  };
  const toggleSubtask = async (id, done) => { await supabase.from("card_subtasks").update({ done }).eq("id", id); setSubtasks(prev => prev.map(s => s.id === id ? { ...s, done } : s)); };
  const addSubtask = async (cardId, title) => { const cs = subtasks.filter(s => s.card_id === cardId); await supabase.from("card_subtasks").insert({ org_id: orgId, card_id: cardId, title, sort_order: cs.length }); await loadAll(); };
  const addComment = async (cardId, body) => { await supabase.from("card_comments").insert({ org_id: orgId, card_id: cardId, user_id: userId, body }); await loadAll(); };
  const addFieldDef = async (name, fieldType, options) => {
    if (!selBoard) {
      console.error("[Projects] addFieldDef: no board selected");
      alert("Please select a board first.");
      return false;
    }
    const payload = { org_id: orgId, board_id: selBoard, name, field_type: fieldType, options, sort_order: boardFieldDefs.length };
    const { error } = await supabase.from("card_field_defs").insert(payload);
    if (error) {
      console.error("[Projects] addFieldDef error:", error.message, error.details, "payload:", payload);
      alert("Could not save field: " + error.message);
      return false;
    }
    await loadAll();
    return true;
  };
  const deleteFieldDef = async (id) => { const { error } = await supabase.from("card_field_defs").delete().eq("id", id); if (error) { console.error("[Projects] deleteFieldDef error:", error); alert("Could not delete field: " + error.message); return; } await loadAll(); };
  const deleteProject = async (id) => { await supabase.from("projects").delete().eq("id", id); setSelProject(null); await loadAll(); };
  const deleteBoard = async (id) => { await supabase.from("project_boards").delete().eq("id", id); setSelBoard(null); await loadAll(); };

  const handleDragStart = (e, card) => { setDragCard(card); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, colId) => { e.preventDefault(); setDragOverCol(colId); };
  // Drop on a column (empty area, or fallback): append to end of column
  const handleDrop = (e, colId) => {
    e.preventDefault();
    if (dragCard) {
      const targetCards = cards.filter(c => c.column_id === colId && !c.archived_at);
      reorderCard(dragCard.id, colId, targetCards.length);
    }
    setDragCard(null); setDragOverCol(null);
  };
  // Drop directly on another card: insert before it
  const handleDropOnCard = (e, targetCard) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragCard || dragCard.id === targetCard.id) { setDragCard(null); setDragOverCol(null); return; }
    const targetCards = cards
      .filter(c => c.column_id === targetCard.column_id && !c.archived_at && c.id !== dragCard.id)
      .sort((a, b) => a.sort_order - b.sort_order);
    const targetIdx = targetCards.findIndex(c => c.id === targetCard.id);
    reorderCard(dragCard.id, targetCard.column_id, targetIdx >= 0 ? targetIdx : targetCards.length);
    setDragCard(null); setDragOverCol(null);
  };
  const handleDragEnd = () => { setDragCard(null); setDragOverCol(null); };

  // ── RENDER ──
  if (loading) return (<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.txt }}><span style={{ fontSize: 13 }}>Loading projects...</span></div>);
  if (projects.length === 0 && !showNewProject) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
      <div style={{ width: 52, height: 52, borderRadius: 8, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="folder" size={26} color={T.blue} /></div>
      <div style={{ fontSize: 16, fontWeight: 400, color: T.txt }}>No projects yet</div>
      <div style={{ fontSize: 12, color: T.txt, maxWidth: 320, textAlign: "center", lineHeight: 1.5 }}>Create your first project to start organizing work with boards, cards, and custom workflows.</div>
      <button onClick={() => setShowNewProject(true)} style={gBtn}>+ New Project</button>
      {showNewProject && <QuickModal title="New Project" onClose={() => setShowNewProject(false)} onSubmit={(name) => createProject(name)} placeholder="Project name" />}
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Sidebar — only show when no project selected */}
      {!selProject && (
      <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0, borderRight: isMobile ? "none" : "1px solid " + T.bdr, background: "#2C2E33", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 14px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 10, fontWeight: 400, color: T.txt, textTransform: "uppercase", letterSpacing: ".08em" }}>Projects</span>
          <button onClick={() => setShowNewProject(true)} style={{ width: 22, height: 22, borderRadius: 6, border: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.06)", color: T.blue, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>+</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 6px 14px" }}>
          {projects.filter(p => p.status !== "archived").map(p => {
            const active = p.id === selProject;
            return (<div key={p.id}
              onContextMenu={e => { e.preventDefault(); setProjectEditModal({ title: "Edit Project", project: p, onSave: ({ name, color }) => { supabase.from("projects").update({ name, color }).eq("id", p.id).then(loadAll); }, onDelete: () => setConfirmModal({ title: "Delete Project", message: 'Delete "' + p.name + '" and all its boards?', onConfirm: () => deleteProject(p.id), danger: true }) }); }}
              onClick={() => setSelProject(p.id)}
              style={{ padding: "7px 10px", borderRadius: 8, cursor: "pointer", marginBottom: 1, display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
                background: active ? T.sidebarActive : "transparent", color: active ? T.blue : T.sidebarTxt }}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setProjectEditModal({
                    title: "Edit Project",
                    project: p,
                    onSave: ({ name, color }) => { supabase.from("projects").update({ name, color }).eq("id", p.id).then(loadAll); },
                    onDelete: () => setConfirmModal({ title: "Delete Project", message: 'Delete "' + p.name + '" and all its boards?', onConfirm: () => deleteProject(p.id), danger: true }),
                  });
                }}
                title="Change color"
                style={{ width: 8, height: 8, borderRadius: 3, background: p.color || T.blue, flexShrink: 0, cursor: "pointer" }}
              />
              <span style={{ fontSize: 12, fontWeight: active ? 700 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{p.name}</span>
            </div>);
          })}
        </div>
      </div>
      )}

      {/* No project selected — show picker */}
      {!selProject && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: 8, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><Icon name="folder" size={26} color={T.blue} /></div>
            <div style={{ fontSize: 15, fontWeight: 400, color: T.txt, marginBottom: 4 }}>Select a project</div>
            <div style={{ fontSize: 12, color: T.txt }}>Choose a project from the sidebar to open its boards</div>
          </div>
        </div>
      )}

      {/* Main — fullscreen when project selected */}
      {selProject && (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ background: "#2C2E33", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
            <div style={{ padding: "10px 16px 0", display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => { setSelProject(null); setSelBoard(null); }} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 8px 4px 0", display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="chevron-left" size={16} color={T.tx3} />
                <span style={{ fontSize: 11, color: T.txt, fontWeight: 400 }}>Projects</span>
              </button>
              <div style={{ width: 1, height: 16, background: T.bdr, margin: "0 4px" }} />
              <div style={{ width: 10, height: 10, borderRadius: 3, background: activeProject?.color || T.blue }} />
              <div style={{ position: "relative" }}>
                <button ref={projectPickerRef} onClick={() => setShowProjectPicker(!showProjectPicker)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                  <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>{activeProject?.name}</span>
                  <Icon name="chevron-down" size={12} color={T.tx3} />
                </button>
                {showProjectPicker && ReactDOM.createPortal(<>
                  <div style={{ position: "fixed", inset: 0, zIndex: 99998 }} onClick={() => setShowProjectPicker(false)} />
                  <div style={{ position: "fixed", top: (projectPickerRef.current?.getBoundingClientRect()?.bottom || 0) + 8, left: projectPickerRef.current?.getBoundingClientRect()?.left || 0, minWidth: 220, background: "#33363D", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.50), 0 0 0 1px rgba(217,219,221,0.05)", zIndex: 99999, overflow: "hidden", padding: "6px" }}>
                    <div style={{ fontSize: 9, fontWeight: 400, color: T.txt, letterSpacing: ".06em", padding: "8px 10px 6px", textTransform: "uppercase" }}>Switch Project</div>
                    {projects.filter(p => p.status !== "archived").map(p => (
                      <button key={p.id} onClick={() => { setSelProject(p.id); setSelBoard(null); setShowProjectPicker(false); }}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", width: "100%", border: "none", borderRadius: 8, background: p.id === selProject ? T.blue : "transparent", color: p.id === selProject ? "#fff" : T.txt, fontSize: 13, fontWeight: p.id === selProject ? 600 : 400, cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}
                        onMouseEnter={e => { if (p.id !== selProject) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                        onMouseLeave={e => { if (p.id !== selProject) e.currentTarget.style.background = "transparent"; }}>
                        <div style={{ width: 8, height: 8, borderRadius: 3, background: p.color || T.blue, flexShrink: 0 }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </>, document.body)}
              </div>
              <button onClick={() => setProjectEditModal({ title: "Edit Project", project: activeProject, onSave: ({ name, color }) => { supabase.from("projects").update({ name, color }).eq("id", selProject).then(loadAll); }, onDelete: () => setConfirmModal({ title: "Delete Project", message: "Delete this project and everything in it?", onConfirm: () => deleteProject(selProject), danger: true }) })} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}><Icon name="edit" size={13} color={T.tx3} /></button>
              <div style={{ flex: 1 }} />
              <button onClick={() => setShowAutomations(true)} style={{ ...gBtn, fontSize: 10, padding: "5px 12px" }}><Icon name="zap" size={12} color="#FAFAFA" /> AUTOMATIONS</button>
              <button onClick={() => setShowFieldManager(true)} style={{ ...gBtn, fontSize: 10, padding: "5px 12px" }}><Icon name="settings" size={12} color="#FAFAFA" /> FIELDS</button>
            </div>
            <div style={{ padding: "6px 16px 0", display: "flex", alignItems: "flex-end" }}>
              <div style={{ display: "flex", gap: 0 }}>
                {projBoards.map(b => {
                  const act = b.id === selBoard;
                  return (<button key={b.id} onClick={() => setSelBoard(b.id)}
                    onContextMenu={e => { e.preventDefault(); setEditModal({ title: "Edit Board", value: b.name, onSave: (n) => { supabase.from("project_boards").update({ name: n }).eq("id", b.id).then(loadAll); }, onDelete: () => setConfirmModal({ title: "Delete Board", message: 'Delete "' + b.name + '"?', onConfirm: () => deleteBoard(b.id), danger: true }) }); }}
                    style={{ padding: "5px 14px 8px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: act ? 700 : 500, color: act ? T.blue : T.tx3, background: "transparent", borderBottom: act ? "2px solid " + T.blue : "2px solid transparent" }}>{b.name}</button>);
                })}
                <button onClick={() => setShowNewBoard(true)} style={{ background: "none", border: "none", cursor: "pointer", color: T.txt, fontSize: 16, padding: "4px 8px" }}>+</button>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ display: "inline-flex", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", overflow: "hidden", marginBottom: 3 }}>
                {[{ id: "kanban", label: "Board" }, { id: "table", label: "Table" }, { id: "calendar", label: "Calendar" }, { id: "timeline", label: "Timeline" }].map((v, i) => (
                  <React.Fragment key={v.id}>
                    {i > 0 && <div style={{ width: 1, background: "rgba(217,219,221,0.12)" }} />}
                    <button onClick={() => setView(v.id)}
                      style={{ display: "inline-flex", alignItems: "center", padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 400,
                        background: view === v.id ? "#4059AD" : "transparent", color: view === v.id ? "#fff" : "#FAFAFA", fontFamily: "inherit" }}>{v.label}</button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        {selBoard && (
          <div style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 8, background: "transparent", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
            <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search cards..." style={{ ...gInput, width: 180 }} />
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={{ ...gInput, cursor: "pointer" }}>
              <option value="all" style={{ background: "#2C2E33" }}>All members</option><option value="unassigned" style={{ background: "#2C2E33" }}>Unassigned</option>
              {teamMembers.map(m => <option key={m._dbId} value={m._dbId} style={{ background: "#2C2E33" }}>{m.name}</option>)}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ ...gInput, cursor: "pointer" }}>
              <option value="all" style={{ background: "#2C2E33" }}>All priorities</option>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k} style={{ background: "#2C2E33" }}>{v}</option>)}
            </select>
            <div style={{ flex: 1 }} /><span style={{ fontSize: 11, color: T.txt }}>{boardCards.length} card{boardCards.length !== 1 ? "s" : ""}</span>
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto" }}>
          {!selBoard ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.txt, fontSize: 13 }}>Select or create a board</div>
          : view === "kanban" ? <KanbanView columns={boardCols} cards={boardCards} getUser={getUser} teamMembers={teamMembers} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} onDropOnCard={handleDropOnCard} onDragEnd={handleDragEnd} dragCard={dragCard} dragOverCol={dragOverCol} createCard={createCard} setSelCard={setSelCard} createColumn={createColumn} getSubtasks={getSubtasks} renameColumn={renameColumn} updateColumn={updateColumn} reorderColumns={reorderColumns} deleteColumn={deleteColumn} setEditModal={setEditModal} setConfirmModal={setConfirmModal} toggleDone={toggleDone} selBoard={selBoard} />
          : view === "table" ? <TableView columns={boardCols} cards={boardCards} fieldDefs={boardFieldDefs} getUser={getUser} getFieldVal={getFieldVal} updateCard={updateCard} updateFieldValue={updateFieldValue} setSelCard={setSelCard} teamMembers={teamMembers} />
          : view === "calendar" ? <CalendarView cards={boardCards} columns={boardCols} getUser={getUser} setSelCard={setSelCard} />
          : <TimelineView cards={boardCards} columns={boardCols} getUser={getUser} setSelCard={setSelCard} />}
        </div>
      </div>
      )}
      {selCard && <CardDetail card={cards.find(c => c.id === selCard)} columns={boardCols} fieldDefs={boardFieldDefs} fieldValues={fieldValues} subtasks={getSubtasks(selCard)} comments={getComments(selCard)} teamMembers={teamMembers} getUser={getUser} updateCard={updateCard} updateFieldValue={updateFieldValue} toggleSubtask={toggleSubtask} addSubtask={addSubtask} addComment={addComment} deleteCard={deleteCard} toggleDone={toggleDone} archiveCard={archiveCard} onClose={() => setSelCard(null)} setConfirmModal={setConfirmModal} orgId={orgId} userId={userId} />}
      {showNewProject && <QuickModal title="New Project" onClose={() => setShowNewProject(false)} onSubmit={(name) => createProject(name)} placeholder="Project name" />}
      {showNewBoard && <QuickModal title="New Board" onClose={() => setShowNewBoard(false)} onSubmit={(name) => createBoard(name)} placeholder="Board name" />}
      {showFieldManager && <FieldManager fieldDefs={boardFieldDefs} addFieldDef={addFieldDef} deleteFieldDef={deleteFieldDef} onClose={() => setShowFieldManager(false)} />}
      {showAutomations && <AutomationsPanel automations={automations.filter(a => a.board_id === selBoard)} columns={boardCols} teamMembers={teamMembers} boardId={selBoard} orgId={orgId} userId={userId} onClose={() => setShowAutomations(false)} reload={loadAll} />}
      {editModal && <EditModal {...editModal} onClose={() => setEditModal(null)} />}
      {projectEditModal && <ProjectEditModal {...projectEditModal} onClose={() => setProjectEditModal(null)} />}
      {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}
    </div>
  );
}

// ── KANBAN VIEW ──
function KanbanView({ columns, cards, getUser, teamMembers, onDragStart, onDragOver, onDrop, onDropOnCard, onDragEnd, dragCard, dragOverCol, createCard, setSelCard, createColumn, getSubtasks, renameColumn, updateColumn, reorderColumns, deleteColumn, setEditModal, setConfirmModal, toggleDone, selBoard }) {
  const [addingTo, setAddingTo] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [showNewCol, setShowNewCol] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [colMenuOpen, setColMenuOpen] = useState(null); // column id of currently open menu
  const [colColorPickerOpen, setColColorPickerOpen] = useState(null); // column id of color picker submenu
  const [editingColId, setEditingColId] = useState(null); // column id being inline-renamed
  const [editingColName, setEditingColName] = useState("");
  const [draggingColId, setDraggingColId] = useState(null); // column being dragged for reorder
  const [dragOverColId, setDragOverColId] = useState(null); // column being hovered as drop target

  // Standard column color palette - 8 swatches plus "no color"
  const COL_COLOR_OPTIONS = [
    { name: "None", value: null },
    { name: "Blue", value: "#4059AD" },
    { name: "Green", value: "#2E8B57" },
    { name: "Orange", value: "#D4763A" },
    { name: "Red", value: "#DC2626" },
    { name: "Purple", value: "#6B5B95" },
    { name: "Teal", value: "#3D8B8B" },
    { name: "Pink", value: "#E91E63" },
    { name: "Yellow", value: "#F59E0B" },
  ];

  // Column-level drag and drop for reordering columns horizontally
  const handleColDragStart = (e, colId) => {
    setDraggingColId(colId);
    e.dataTransfer.effectAllowed = "move";
    // Use a custom data type so we can distinguish column drags from card drags
    e.dataTransfer.setData("text/column-id", colId);
  };
  const handleColDragOver = (e, colId) => {
    if (draggingColId && draggingColId !== colId) {
      e.preventDefault();
      e.stopPropagation();
      setDragOverColId(colId);
    }
  };
  const handleColDrop = (e, targetColId) => {
    if (!draggingColId || draggingColId === targetColId) {
      setDraggingColId(null); setDragOverColId(null);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Build the new ordering by removing the dragged col and inserting at target position
    const sortedCols = [...columns].sort((a, b) => a.sort_order - b.sort_order);
    const draggedIdx = sortedCols.findIndex(c => c.id === draggingColId);
    const targetIdx = sortedCols.findIndex(c => c.id === targetColId);
    if (draggedIdx < 0 || targetIdx < 0) { setDraggingColId(null); setDragOverColId(null); return; }
    const reordered = [...sortedCols];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    reorderColumns(selBoard, reordered.map(c => c.id));
    setDraggingColId(null); setDragOverColId(null);
  };
  const handleColDragEnd = () => { setDraggingColId(null); setDragOverColId(null); };

  // Trello-like column background — slightly elevated panel
  const TRELLO_COL_BG = "rgba(255,255,255,0.04)";
  const TRELLO_COL_BG_HOVER = "rgba(64,89,173,0.10)";
  const TRELLO_COL_BG_DRAGOVER = "rgba(64,89,173,0.20)";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Drop columns — Trello-style horizontal scroll with bg panels */}
      <div style={{ display: "flex", gap: 12, padding: "12px 16px", flex: 1, overflowX: "auto", overflowY: "hidden", alignItems: "flex-start" }}>
      {columns.map(col => {
        const cc = cards.filter(c => c.column_id === col.id).sort((a, b) => a.sort_order - b.sort_order);
        const over = dragOverCol === col.id;
        const colOver = dragOverColId === col.id;
        const menuOpen = colMenuOpen === col.id;
        const colorPickerOpen = colColorPickerOpen === col.id;
        const isEditing = editingColId === col.id;
        const isBeingDragged = draggingColId === col.id;
        return (<div key={col.id}
          onDragOver={(e) => {
            // If we're dragging a COLUMN, handle column-reorder drop target
            if (draggingColId) { handleColDragOver(e, col.id); return; }
            // Otherwise, this is a CARD drag — use card drop logic
            onDragOver(e, col.id);
          }}
          onDrop={(e) => {
            if (draggingColId) { handleColDrop(e, col.id); return; }
            onDrop(e, col.id);
          }}
          style={{ width: 280, minWidth: 280, flexShrink: 0, display: "flex", flexDirection: "column",
            borderRadius: 10, background: colOver ? TRELLO_COL_BG_DRAGOVER : (over ? TRELLO_COL_BG_HOVER : TRELLO_COL_BG),
            border: "1px solid " + (colOver ? "rgba(64,89,173,0.55)" : (over ? "rgba(64,89,173,0.35)" : "rgba(217,219,221,0.06)")),
            transition: "background 0.15s, border 0.15s, opacity 0.15s",
            opacity: isBeingDragged ? 0.4 : 1,
            maxHeight: "calc(100vh - 220px)", padding: "10px 8px 8px" }}>
          {/* Column header with title, count, and menu — also acts as drag handle for reorder */}
          <div
            draggable
            onDragStart={(e) => handleColDragStart(e, col.id)}
            onDragEnd={handleColDragEnd}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 6px 10px", position: "relative", cursor: isEditing ? "text" : "grab" }}
          >
            {/* Color dot — inherits from col.color */}
            <div style={{ width: 8, height: 8, borderRadius: 4, background: col.color || T.blue, flexShrink: 0 }} />
            {isEditing ? (
              <input
                autoFocus
                value={editingColName}
                onChange={e => setEditingColName(e.target.value)}
                onBlur={() => {
                  if (editingColName.trim() && editingColName.trim() !== col.name) {
                    renameColumn(col.id, editingColName.trim());
                  }
                  setEditingColId(null);
                  setEditingColName("");
                }}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    if (editingColName.trim() && editingColName.trim() !== col.name) {
                      renameColumn(col.id, editingColName.trim());
                    }
                    setEditingColId(null);
                    setEditingColName("");
                  }
                  if (e.key === "Escape") {
                    setEditingColId(null);
                    setEditingColName("");
                  }
                }}
                style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#FAFAFA", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(64,89,173,0.5)", borderRadius: 4, padding: "3px 6px", outline: "none", textTransform: "uppercase", letterSpacing: ".04em", fontFamily: "inherit" }}
              />
            ) : (
              <span
                onClick={() => { setEditingColId(col.id); setEditingColName(col.name); }}
                style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#FAFAFA", textTransform: "uppercase", letterSpacing: ".04em", cursor: "pointer", padding: "3px 0" }}
                title="Click to rename"
              >{col.name}</span>
            )}
            <span style={{ fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.45)", padding: "0 4px" }}>{cc.length}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setColMenuOpen(menuOpen ? null : col.id); }}
              style={{ width: 22, height: 22, borderRadius: 6, border: "none", background: menuOpen ? "rgba(255,255,255,0.10)" : "transparent", color: T.txt, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, lineHeight: 1, padding: 0 }}
              title="Column actions"
            >⋯</button>
            {menuOpen && (
              <>
                {/* Click-outside catcher */}
                <div onClick={() => setColMenuOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
                {/* Menu */}
                <div style={{ position: "absolute", top: 30, right: 4, background: "#3A3D44", border: "1px solid rgba(217,219,221,0.15)", borderRadius: 8, boxShadow: "0 8px 30px rgba(0,0,0,0.40)", padding: 4, zIndex: 100, minWidth: 180 }}>
                  <button
                    onClick={() => { setColMenuOpen(null); setEditingColId(col.id); setEditingColName(col.name); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 12px", border: "none", background: "transparent", color: T.txt, fontSize: 12, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >Rename</button>
                  <button
                    onClick={() => { setColMenuOpen(null); setAddingTo(col.id); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 12px", border: "none", background: "transparent", color: T.txt, fontSize: 12, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >Add card</button>
                  <div style={{ height: 1, background: "rgba(217,219,221,0.10)", margin: "4px 0" }} />
                  {/* Column color picker — inline swatches */}
                  <div style={{ padding: "6px 12px 4px", fontSize: 9, fontWeight: 600, color: T.tx3, textTransform: "uppercase", letterSpacing: ".06em" }}>Column color</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, padding: "0 12px 6px" }}>
                    {COL_COLOR_OPTIONS.map(c => {
                      const selected = (col.color || null) === c.value;
                      return (
                        <button
                          key={c.name}
                          onClick={() => { updateColumn(col.id, { color: c.value }); }}
                          title={c.name}
                          style={{
                            width: 22, height: 18, borderRadius: 4,
                            background: c.value || "transparent",
                            border: c.value
                              ? (selected ? "2px solid #FAFAFA" : "1px solid rgba(217,219,221,0.20)")
                              : "1px dashed rgba(217,219,221,0.30)",
                            cursor: "pointer", padding: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          {!c.value && <span style={{ fontSize: 10, color: T.tx3, lineHeight: 1 }}>∅</span>}
                          {selected && c.value && <span style={{ color: "#fff", fontSize: 9, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ height: 1, background: "rgba(217,219,221,0.10)", margin: "4px 0" }} />
                  <button
                    onClick={() => {
                      setColMenuOpen(null);
                      const msg = cc.length > 0
                        ? `Delete column "${col.name}" and its ${cc.length} card${cc.length === 1 ? "" : "s"}? This cannot be undone.`
                        : `Delete column "${col.name}"?`;
                      setConfirmModal({ title: "Delete Column", message: msg, onConfirm: () => deleteColumn(col.id), danger: true });
                    }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 12px", border: "none", background: "transparent", color: "#E53935", fontSize: 12, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(229,57,53,0.10)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >Delete column</button>
                </div>
              </>
            )}
          </div>

          {/* Cards scroll area */}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, padding: "0 2px" }}>
            {cc.map(card => {
              const assigneeIds = getCardAssignees(card);
              const assignees = assigneeIds.map(id => getUser(id)).filter(Boolean);
              const subs = getSubtasks(card.id); const done = subs.filter(s => s.done).length;
              const isDone = !!card.completed_at;
              const cover = card.cover_color;
              return (<div
                key={card.id}
                draggable
                onDragStart={(e) => onDragStart(e, card)}
                onDragEnd={onDragEnd}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => onDropOnCard(e, card)}
                onClick={() => setSelCard(card.id)}
                style={{ background: "#363941", borderRadius: 8, cursor: "grab", border: "1px solid rgba(217,219,221,0.10)",
                  opacity: dragCard?.id === card.id ? 0.35 : isDone ? 0.55 : 1,
                  boxShadow: "0 1px 0 rgba(0,0,0,0.18)", transition: "all 0.12s",
                  // Don't clip inside the card — text needs to wrap, not get cut off
                  overflow: "visible" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(217,219,221,0.30)"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(217,219,221,0.10)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 1px 0 rgba(0,0,0,0.18)"; }}>
                {/* Trello-style cover color stripe */}
                {cover && <div style={{ height: 30, background: cover, borderTopLeftRadius: 7, borderTopRightRadius: 7 }} />}
                <div style={{ padding: "9px 12px 10px" }}>
                  {card.labels?.length > 0 && <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 6 }}>{card.labels.map((l, i) => l.name ? <Pill key={i} color={l.color} small>{l.name}</Pill> : <div key={i} style={{ width: 32, height: 6, borderRadius: 3, background: l.color }} />)}</div>}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <button onClick={(e) => { e.stopPropagation(); toggleDone(card.id); }}
                      style={{ width: 18, height: 18, borderRadius: 6, border: "1.5px solid " + (isDone ? "#2E8B57" : "rgba(217,219,221,0.20)"), background: isDone ? "#2E8B57" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all 0.12s" }}
                      onMouseEnter={e => { if (!isDone) e.currentTarget.style.borderColor = "#2E8B57"; }}
                      onMouseLeave={e => { if (!isDone) e.currentTarget.style.borderColor = "rgba(217,219,221,0.30)"; }}>
                      {isDone && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 400, color: isDone ? T.tx3 : "#FAFAFA", lineHeight: 1.4, textDecoration: isDone ? "line-through" : "none", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal" }}>{card.title}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, flexWrap: "wrap" }}>
                        <PriorityDot priority={card.priority} />
                        {card.due_date && (
                          <span style={{ fontSize: 10, fontWeight: 400, color: isDone ? T.tx4 : new Date(card.due_date) < new Date() ? "#E53935" : T.tx3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <Icon name="calendar" size={10} color={isDone ? T.tx4 : new Date(card.due_date) < new Date() ? "#E53935" : T.tx3} />
                            {fmtDate(card.due_date)}
                          </span>
                        )}
                        {subs.length > 0 && (
                          <span style={{ fontSize: 10, fontWeight: 400, color: done === subs.length ? "#2E8B57" : T.tx3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <Icon name="check" size={10} color={done === subs.length ? "#2E8B57" : T.tx3} />
                            {done}/{subs.length}
                          </span>
                        )}
                        <div style={{ flex: 1 }} />
                        {/* Stacked assignee avatars (Trello-style) */}
                        {assignees.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {assignees.slice(0, 3).map((a, i) => (
                              <div key={a._dbId || i} style={{
                                width: 22, height: 22, borderRadius: "50%",
                                background: T.blue,
                                border: "2px solid #363941",
                                marginLeft: i === 0 ? 0 : -7,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 9, fontWeight: 600, color: "#fff",
                                position: "relative", zIndex: 10 - i,
                              }} title={a.name}>
                                {(a.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                              </div>
                            ))}
                            {assignees.length > 3 && (
                              <div style={{
                                width: 22, height: 22, borderRadius: "50%",
                                background: "#4A4D55",
                                border: "2px solid #363941",
                                marginLeft: -7,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 9, fontWeight: 600, color: "#fff",
                              }}>+{assignees.length - 3}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>);
            })}
            {addingTo === col.id ? (
              <div style={{ background: "#363941", borderRadius: 8, padding: 8, border: "1px solid rgba(64,89,173,0.5)" }}>
                <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Enter a title for this card..."
                  onKeyDown={e => { if (e.key === "Enter" && newTitle.trim()) { createCard(col.id, newTitle.trim()); setNewTitle(""); } if (e.key === "Escape") { setAddingTo(null); setNewTitle(""); } }}
                  style={{ ...gInput, width: "100%", border: "none", background: "transparent", fontSize: 13 }} />
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <button onClick={() => { if (newTitle.trim()) { createCard(col.id, newTitle.trim()); setNewTitle(""); }}} style={{ ...gBtnPrimary, fontSize: 10, padding: "5px 14px" }}>Add card</button>
                  <button onClick={() => { setAddingTo(null); setNewTitle(""); }} style={{ background: "transparent", border: "none", color: T.tx3, cursor: "pointer", fontSize: 16, padding: "0 6px" }}>×</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingTo(col.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: T.tx3, fontSize: 12, fontWeight: 400, padding: "8px 6px", textAlign: "left", borderRadius: 6, marginTop: 2, transition: "all 0.12s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#FAFAFA"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.tx3; }}>+ Add a card</button>
            )}
          </div>
        </div>);
      })}
      {/* Add column tile */}
      <div style={{ width: 280, minWidth: 280, flexShrink: 0 }}>
        {showNewCol ? (
          <div style={{ background: TRELLO_COL_BG, border: "1px solid rgba(64,89,173,0.35)", borderRadius: 10, padding: 10 }}>
            <input autoFocus value={newColName} onChange={e => setNewColName(e.target.value)} placeholder="Enter list title..."
              onKeyDown={e => { if (e.key === "Enter" && newColName.trim()) { createColumn(newColName.trim()); setNewColName(""); setShowNewCol(false); } if (e.key === "Escape") setShowNewCol(false); }}
              style={{ ...gInput, width: "100%", marginBottom: 8, fontSize: 12 }} />
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => { if (newColName.trim()) { createColumn(newColName.trim()); setNewColName(""); setShowNewCol(false); }}} style={{ ...gBtnPrimary, fontSize: 10, padding: "5px 14px" }}>Add list</button>
              <button onClick={() => { setShowNewCol(false); setNewColName(""); }} style={{ background: "transparent", border: "none", color: T.tx3, cursor: "pointer", fontSize: 16, padding: "0 6px" }}>×</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowNewCol(true)} style={{ background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(217,219,221,0.20)", borderRadius: 10, padding: "12px 14px", cursor: "pointer", color: T.txt, fontSize: 12, fontWeight: 400, width: "100%", textAlign: "left", transition: "all 0.12s", fontFamily: "inherit" }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.borderColor = "rgba(64,89,173,0.40)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(217,219,221,0.20)"; }}>
            + Add another list
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

// ── TABLE VIEW ──
function TableView({ columns, cards, fieldDefs, getUser, getFieldVal, updateCard, updateFieldValue, setSelCard, teamMembers }) {
  const allCards = cards.sort((a, b) => { const ca = columns.find(c => c.id === a.column_id); const cb = columns.find(c => c.id === b.column_id); return (ca?.sort_order || 0) - (cb?.sort_order || 0) || a.sort_order - b.sort_order; });
  const ci = { ...gInput, fontSize: 11, padding: "3px 6px", borderRadius: 5 };
  return (
    <div style={{ padding: 16, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
        <thead><tr>
          <th style={thS}>Title</th><th style={{ ...thS, width: 120 }}>Status</th><th style={{ ...thS, width: 120 }}>Assignee</th>
          <th style={{ ...thS, width: 90 }}>Priority</th><th style={{ ...thS, width: 100 }}>Due Date</th>
          {fieldDefs.map(fd => <th key={fd.id} style={{ ...thS, width: fd.width || 140 }}>{fd.name}</th>)}
        </tr></thead>
        <tbody>{allCards.map(card => {
          const col = columns.find(c => c.id === card.column_id);
          return (<tr key={card.id} onMouseEnter={e => e.currentTarget.style.background = "rgba(64,89,173,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <td style={tdS} onClick={() => setSelCard(card.id)}><span style={{ fontWeight: 400, color: T.txt, cursor: "pointer" }}>{card.title}</span></td>
            <td style={tdS}><select value={card.column_id} onChange={e => updateCard(card.id, { column_id: e.target.value })} style={{ ...ci, background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontWeight: 400 }}>{columns.map(c => <option key={c.id} value={c.id} style={{ background: "#2C2E33" }}>{c.name}</option>)}</select></td>
            <td style={tdS}><select value={card.assignee_id || ""} onChange={e => updateCard(card.id, { assignee_id: e.target.value || null })} style={{ ...ci, background: "rgba(255,255,255,0.06)", color: "#FAFAFA" }}><option value="" style={{ background: "#2C2E33" }}>Unassigned</option>{teamMembers.map(m => <option key={m._dbId} value={m._dbId} style={{ background: "#2C2E33" }}>{m.name}</option>)}</select></td>
            <td style={tdS}><select value={card.priority} onChange={e => updateCard(card.id, { priority: e.target.value })} style={{ ...ci, background: "rgba(255,255,255,0.06)", color: PRIORITY_COLORS[card.priority], fontWeight: 400 }}>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k} style={{ background: "#2C2E33", color: "#FAFAFA" }}>{v}</option>)}</select></td>
            <td style={tdS}><input type="date" value={card.due_date || ""} onChange={e => updateCard(card.id, { due_date: e.target.value || null })} style={{ ...ci, colorScheme: "dark" }} /></td>
            {fieldDefs.map(fd => <td key={fd.id} style={tdS}><FieldCell def={fd} value={getFieldVal(card.id, fd.id)} onChange={(v) => updateFieldValue(card.id, fd.id, v)} teamMembers={teamMembers} /></td>)}
          </tr>);
        })}</tbody>
      </table>
      {allCards.length === 0 && <div style={{ textAlign: "center", padding: 40, color: T.txt, fontSize: 12 }}>No cards yet. Add cards in the Board view.</div>}
    </div>
  );
}
const thS = { padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 400, color: T.txt, position: "sticky", top: 0, background: T.bg, borderBottom: "1px solid rgba(217,219,221,0.08)", textTransform: "uppercase", letterSpacing: ".06em" };
const tdS = { padding: "6px 10px", verticalAlign: "middle", borderBottom: "1px solid rgba(217,219,221,0.08)" };

function FieldCell({ def, value, onChange, teamMembers }) {
  const v = value?.value_text || value?.value_number || value?.value_date || "";
  const ci = { ...gInput, fontSize: 11, padding: "3px 6px", borderRadius: 5, width: "100%" };
  switch (def.field_type) {
    case "text": case "url": case "email": case "phone": return <input value={v} onChange={e => onChange(e.target.value)} style={ci} placeholder="-" />;
    case "number": case "currency": case "percent": case "rating": return <input type="number" value={v} onChange={e => onChange(parseFloat(e.target.value) || 0)} style={{ ...ci, width: 70 }} />;
    case "date": return <input type="date" value={v} onChange={e => onChange(e.target.value)} style={ci} />;
    case "checkbox": return <Checkbox checked={v === "true"} onChange={e => onChange(e.target.checked ? "true" : "false")} />;
    case "dropdown": return <select value={v} onChange={e => onChange(e.target.value)} style={ci}><option value="" style={{ background: "#2C2E33" }}>-</option>{(def.options || []).map(o => <option key={o} value={o} style={{ background: "#2C2E33" }}>{o}</option>)}</select>;
    case "person": return <select value={v} onChange={e => onChange(e.target.value)} style={ci}><option value="" style={{ background: "#2C2E33" }}>-</option>{teamMembers.map(m => <option key={m._dbId} value={m._dbId} style={{ background: "#2C2E33" }}>{m.name}</option>)}</select>;
    case "color": return <input type="color" value={v || T.blue} onChange={e => onChange(e.target.value)} style={{ width: 28, height: 22, border: "none", cursor: "pointer", borderRadius: 4 }} />;
    default: return <input value={v} onChange={e => onChange(e.target.value)} style={ci} placeholder="-" />;
  }
}

// ── CALENDAR VIEW ──
function CalendarView({ cards, columns, getUser, setSelCard }) {
  const [month, setMonth] = useState(new Date());
  const year = month.getFullYear(); const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay(); const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const today = new Date().toISOString().split("T")[0]; const datedCards = cards.filter(c => c.due_date);
  const days = []; for (let i = 0; i < firstDay; i++) days.push(null); for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return (<div style={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <button onClick={() => setMonth(new Date(year, mo - 1))} style={{ ...gBtn, fontSize: 9, padding: "3px 8px" }}>&#8249;</button>
      <span style={{ fontSize: 14, fontWeight: 400, color: T.txt, minWidth: 160, textAlign: "center" }}>{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
      <button onClick={() => setMonth(new Date(year, mo + 1))} style={{ ...gBtn, fontSize: 9, padding: "3px 8px" }}>&#8250;</button>
      <button onClick={() => setMonth(new Date())} style={{ ...gBtn, fontSize: 9 }}>Today</button>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, background: "rgba(217,219,221,0.08)", borderRadius: 8, overflow: "hidden" }}>
      {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} style={{ padding: 6, fontSize: 10, fontWeight: 400, color: "rgba(250,250,250,0.35)", textAlign: "center", background: "rgba(255,255,255,0.03)", textTransform: "uppercase", letterSpacing: ".06em" }}>{d}</div>)}
      {days.map((day, i) => {
        if (!day) return <div key={"e"+i} style={{ background: "rgba(255,255,255,0.02)", minHeight: 80 }} />;
        const ds = year+"-"+String(mo+1).padStart(2,"0")+"-"+String(day).padStart(2,"0");
        const dc = datedCards.filter(c => c.due_date === ds); const isT = ds === today;
        return (<div key={i} style={{ minHeight: 80, padding: 4, background: "rgba(255,255,255,0.03)", cursor: "pointer" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"} onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}>
          <div style={{ fontSize: 11, fontWeight: isT ? 700 : 400, color: isT ? "#fff" : T.txt, textAlign: "right", padding: "0 2px 2px" }}>
            {isT ? <span style={{ background: T.blue, color: "#fff", borderRadius: "50%", width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 400 }}>{day}</span> : day}
          </div>
          {dc.slice(0,3).map(card => { const col = columns.find(c => c.id === card.column_id); return (
            <div key={card.id} onClick={() => setSelCard(card.id)} style={{ fontSize: 9, padding: "2px 5px", borderRadius: 3, marginBottom: 1, cursor: "pointer", background: col?.color || T.blue, color: "#fff", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.title}</div>); })}
          {dc.length > 3 && <div style={{ fontSize: 9, color: T.tx3 }}>+{dc.length - 3}</div>}
        </div>);
      })}
    </div>
  </div>);
}

// ── TIMELINE VIEW ──
function TimelineView({ cards, columns, getUser, setSelCard }) {
  const [wo, setWo] = useState(0); const today = new Date(); const start = new Date(today); start.setDate(start.getDate() - start.getDay() + wo * 7);
  const td = 28; const dw = 36;
  const dc = cards.filter(c => c.start_date || c.due_date).map(c => ({ ...c, _s: c.start_date ? new Date(c.start_date) : c.due_date ? new Date(c.due_date) : null, _e: c.due_date ? new Date(c.due_date) : c.start_date ? new Date(c.start_date) : null })).sort((a, b) => (a._s || 0) - (b._s || 0));
  const dh = []; for (let i = 0; i < td; i++) { const d = new Date(start); d.setDate(d.getDate() + i); dh.push(d); }
  const ts = today.toISOString().split("T")[0];
  return (<div style={{ padding: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <button onClick={() => setWo(w => w - 1)} style={{ ...gBtn, fontSize: 9, padding: "3px 8px" }}>&#8249;</button>
      <button onClick={() => setWo(0)} style={{ ...gBtn, fontSize: 9 }}>Today</button>
      <button onClick={() => setWo(w => w + 1)} style={{ ...gBtn, fontSize: 9, padding: "3px 8px" }}>&#8250;</button>
    </div>
    <div style={{ overflowX: "auto", ...{ background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }, borderRadius: 8, padding: 0 }}>
      <div style={{ display: "flex", minWidth: td * dw + 200 }}>
        <div style={{ width: 200, flexShrink: 0, borderRight: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)" }}>
          <div style={{ height: 40, borderBottom: "1px solid rgba(217,219,221,0.08)", padding: "8px 10px", fontSize: 10, fontWeight: 400, color: T.txt, display: "flex", alignItems: "center", textTransform: "uppercase", letterSpacing: ".06em" }}>Card</div>
          {dc.map(card => (<div key={card.id} onClick={() => setSelCard(card.id)} style={{ height: 36, padding: "0 10px", borderBottom: "1px solid rgba(217,219,221,0.08)", fontSize: 11, color: T.txt, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}><PriorityDot priority={card.priority} size={6} />{card.title}</div>))}
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          <div style={{ display: "flex", height: 40, borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
            {dh.map((d, i) => { const ds = d.toISOString().split("T")[0]; const isT = ds === ts; const isW = d.getDay() === 0 || d.getDay() === 6; return (
              <div key={i} style={{ width: dw, flexShrink: 0, textAlign: "center", borderRight: "1px solid " + T.bdr, background: isT ? T.blueG : isW ? T.bgAlt : "transparent", padding: "4px 0" }}>
                <div style={{ fontSize: 8, color: T.txt, fontWeight: 400, textTransform: "uppercase" }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                <div style={{ fontSize: 11, fontWeight: isT ? 700 : 400, color: isT ? T.blue : T.txt }}>{d.getDate()}</div>
              </div>); })}
          </div>
          {dc.map(card => {
            const sd = daysBetween(start, card._s); const dur = Math.max(1, daysBetween(card._s, card._e) + 1);
            const left = clamp(sd, 0, td) * dw; const width = clamp(dur, 0, td - Math.max(0, sd)) * dw;
            const col = columns.find(c => c.id === card.column_id); const bc = col?.color || T.blue;
            if (sd + dur < 0 || sd >= td) return <div key={card.id} style={{ height: 36, borderBottom: "1px solid rgba(217,219,221,0.08)" }} />;
            return (<div key={card.id} style={{ height: 36, borderBottom: "1px solid rgba(217,219,221,0.08)", position: "relative" }}>
              {dh.map((_, i) => <div key={i} style={{ position: "absolute", left: i * dw, top: 0, width: dw, height: "100%", borderRight: "1px solid " + T.bdr, background: dh[i]?.toISOString().split("T")[0] === ts ? T.blueG : "transparent" }} />)}
              {width > 0 && <div onClick={() => setSelCard(card.id)} style={{ position: "absolute", left, top: 6, width: Math.max(width - 4, 8), height: 24, background: bc + "20", border: "1px solid " + bc + "40", borderRadius: 5, cursor: "pointer", display: "flex", alignItems: "center", padding: "0 6px", overflow: "hidden" }}>
                <span style={{ fontSize: 9, fontWeight: 400, color: bc, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{card.title}</span>
              </div>}
            </div>);
          })}
        </div>
      </div>
    </div>
    {dc.length === 0 && <div style={{ textAlign: "center", padding: 40, color: T.txt, fontSize: 12 }}>Set start and due dates on cards to see them here.</div>}
  </div>);
}

// ── CARD DETAIL MODAL ──
function CardDetail({ card, columns, fieldDefs, fieldValues, subtasks, comments, teamMembers, getUser, updateCard, updateFieldValue, toggleSubtask, addSubtask, addComment, deleteCard, toggleDone, archiveCard, onClose, setConfirmModal, orgId, userId }) {
  const [newSub, setNewSub] = useState(""); const [newComment, setNewComment] = useState("");
  const [editTitle, setEditTitle] = useState(false); const [title, setTitle] = useState(card?.title || "");
  const [desc, setDesc] = useState(card?.description || "");
  // Routed through /api/notifications/send so it respects notification_preferences
  // (global_mute, quiet_hours, channel_preferences.project per recipient). The
  // old direct-insert path bypassed the gate entirely; the "task" fallback that
  // used to live here is no longer needed since migration 062 added "project"
  // as a real category in channel_preferences.
  const notify = async (toUserId, ntitle, body) => {
    if (!toUserId || !orgId) return;
    try {
      const session = (await supabase.auth.getSession())?.data?.session;
      const token = session?.access_token;
      if (!token) {
        console.warn("[Projects] No session token, skipping notify");
        return;
      }
      const res = await fetch("/api/notifications/send", {
        method: "POST",
        headers: {
          "Authorization": "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          org_id: orgId,
          user_id: toUserId,
          category: "project",
          title: ntitle,
          body: body,
          icon: "folder",
          severity: "info",
          action_type: "board_card",
          action_id: card.id,
        }),
      });
      if (!res.ok) {
        console.warn("[Projects] Notify failed:", res.status, await res.text());
      }
    } catch (e) {
      console.warn("[Projects] Notify error:", e.message);
    }
  };
  const handleAddAssignee = async (newUserId) => {
    if (!newUserId) return;
    const current = getCardAssignees(card);
    if (current.includes(newUserId)) return; // already assigned
    const next = [...current, newUserId];
    await updateCard(card.id, { assignee_ids: next });
    notify(newUserId, "Assigned to you: " + card.title, "You've been assigned to \"" + card.title + "\"");
  };
  const handleRemoveAssignee = async (removeUserId) => {
    const current = getCardAssignees(card);
    const next = current.filter(id => id !== removeUserId);
    await updateCard(card.id, { assignee_ids: next });
  };
  const handleAddSubtask = async (stitle) => {
    await addSubtask(card.id, stitle);
    // Notify all assignees, not just the legacy single one
    getCardAssignees(card).forEach(uid => notify(uid, "New subtask on: " + card.title, "Subtask \"" + stitle + "\" was added to \"" + card.title + "\""));
  };
  const handleStatusChange = async (newColId) => {
    const newCol = columns.find(c => c.id === newColId);
    await updateCard(card.id, { column_id: newColId });
    getCardAssignees(card).forEach(uid => notify(uid, card.title + " moved to " + (newCol?.name || ""), "Card \"" + card.title + "\" was moved to " + (newCol?.name || "a new column")));
  };
  const handleAddComment = async (body) => {
    await addComment(card.id, body);
    getCardAssignees(card).forEach(uid => notify(uid, "Comment on: " + card.title, body.substring(0, 100)));
  };
  if (!card) return null;
  const col = columns.find(c => c.id === card.column_id);
  const doneSubs = subtasks.filter(s => s.done).length; const subPct = subtasks.length > 0 ? (doneSubs / subtasks.length) * 100 : 0;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.50)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 40, overflowY: "auto" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 700, maxWidth: "95vw", background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: 0, overflow: "hidden", boxShadow: "0 40px 100px rgba(0,0,0,0.40)", marginBottom: 40, maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
        <div style={{ padding: "14px 20px 10px", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {editTitle ? <input autoFocus value={title} onChange={e => setTitle(e.target.value)} onBlur={() => { updateCard(card.id, { title }); setEditTitle(false); }} onKeyDown={e => { if (e.key === "Enter") { updateCard(card.id, { title }); setEditTitle(false); } }} style={{ flex: 1, fontSize: 16, fontWeight: 400, border: "none", outline: "none", color: T.txt, padding: 0, background: "transparent" }} />
            : <h2 onClick={() => setEditTitle(true)} style={{ flex: 1, fontSize: 16, fontWeight: 400, color: T.txt, margin: 0, cursor: "pointer" }}>{card.title}</h2>}
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.txt, fontSize: 14 }}>×</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
            <div style={{ width: 7, height: 7, borderRadius: 2, background: T.blue }} />
            <span style={{ fontSize: 11, color: T.txt }}>in <strong>{col?.name}</strong></span>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={{ flex: 1, padding: 20 }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 400, color: T.txt, marginBottom: 6 }}>Description</div>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} onBlur={() => updateCard(card.id, { description: desc })} placeholder="Add a description..." rows={3}
                style={{ width: "100%", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, padding: 10, fontSize: 12, resize: "vertical", outline: "none", fontFamily: "inherit", lineHeight: 1.5, color: T.txt, background: "rgba(255,255,255,0.06)" }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 400, color: T.txt }}>Subtasks</span>
                {subtasks.length > 0 && <span style={{ fontSize: 10, color: T.txt }}>{doneSubs}/{subtasks.length}</span>}
              </div>
              {subtasks.length > 0 && <ProgressBar value={doneSubs} max={subtasks.length} color={subPct === 100 ? "#2E8B57" : T.blue} height={4} />}
              <div style={{ marginTop: 6 }}>{subtasks.map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                  <Checkbox checked={s.done} onChange={() => toggleSubtask(s.id, !s.done)} />
                  <span style={{ fontSize: 12, color: s.done ? T.tx3 : T.txt, textDecoration: s.done ? "line-through" : "none", flex: 1 }}>{s.title}</span>
                </div>))}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input value={newSub} onChange={e => setNewSub(e.target.value)} placeholder="Add subtask..." onKeyDown={e => { if (e.key === "Enter" && newSub.trim()) { handleAddSubtask(newSub.trim()); setNewSub(""); } }} style={{ ...gInput, flex: 1, fontSize: 11 }} />
                <button onClick={() => { if (newSub.trim()) { handleAddSubtask(newSub.trim()); setNewSub(""); } }} style={{ ...gBtnPrimary, fontSize: 9, padding: "4px 10px" }}>Add</button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Activity</div>
              {comments.map(cm => { const u = getUser(cm.user_id); return (
                <div key={cm.id} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: T.txt }}><strong style={{ color: T.txt }}>{u?.name || "System"}</strong> &middot; {fmtDateFull(cm.created_at)}</div>
                    <div style={{ fontSize: 12, color: cm.type === "system" ? T.tx3 : T.txt, fontStyle: cm.type === "system" ? "italic" : "normal", marginTop: 2, lineHeight: 1.4 }}>{cm.body}</div>
                  </div>
                </div>); })}
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Write a comment..." onKeyDown={e => { if (e.key === "Enter" && newComment.trim()) { handleAddComment(newComment.trim()); setNewComment(""); } }} style={{ ...gInput, flex: 1, fontSize: 11 }} />
                <button onClick={() => { if (newComment.trim()) { handleAddComment(newComment.trim()); setNewComment(""); } }} style={{ ...gBtnPrimary, fontSize: 9, padding: "4px 10px" }}>Post</button>
              </div>
            </div>
          </div>
          <div style={{ width: 210, padding: "20px 14px", borderLeft: "1px solid rgba(217,219,221,0.08)", background: "rgba(255,255,255,0.02)" }}>
            <SideField label="Status"><select value={card.column_id} onChange={e => handleStatusChange(e.target.value)} style={{ ...sideInput, fontWeight: 400 }}>{columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></SideField>
            <SideField label="Assignees">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                {getCardAssignees(card).map(uid => {
                  const u = getUser(uid);
                  if (!u) return null;
                  return (
                    <div key={uid} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 4px 3px 3px", borderRadius: 12, background: "rgba(64,89,173,0.18)", border: "1px solid rgba(64,89,173,0.30)" }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: T.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 600, color: "#fff" }}>
                        {(u.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <span style={{ fontSize: 10, color: T.txt }}>{u.name}</span>
                      <button onClick={() => handleRemoveAssignee(uid)} style={{ background: "transparent", border: "none", color: T.tx3, cursor: "pointer", fontSize: 12, lineHeight: 1, padding: "0 3px", fontFamily: "inherit" }} title="Remove">×</button>
                    </div>
                  );
                })}
              </div>
              <select
                value=""
                onChange={e => { if (e.target.value) { handleAddAssignee(e.target.value); e.target.value = ""; } }}
                style={sideInput}
              >
                <option value="">+ Add assignee...</option>
                {teamMembers.filter(m => !getCardAssignees(card).includes(m._dbId)).map(m => <option key={m._dbId} value={m._dbId}>{m.name}</option>)}
              </select>
            </SideField>
            <SideField label="Cover">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {COVER_COLORS.map(c => {
                  const selected = (card.cover_color || null) === c.value;
                  return (
                    <button
                      key={c.name}
                      onClick={() => updateCard(card.id, { cover_color: c.value })}
                      title={c.name}
                      style={{
                        width: 28, height: 22, borderRadius: 5,
                        background: c.value || "transparent",
                        border: c.value
                          ? (selected ? "2px solid #FAFAFA" : "1px solid rgba(217,219,221,0.20)")
                          : "1px dashed rgba(217,219,221,0.30)",
                        cursor: "pointer",
                        position: "relative",
                        padding: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {!c.value && <span style={{ fontSize: 14, color: T.tx3, lineHeight: 1 }}>∅</span>}
                      {selected && c.value && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </SideField>
            <SideField label="Priority"><select value={card.priority} onChange={e => updateCard(card.id, { priority: e.target.value })} style={{ ...sideInput, fontWeight: 400 }}>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></SideField>
            <SideField label="Start Date"><input type="date" value={card.start_date || ""} onChange={e => updateCard(card.id, { start_date: e.target.value || null })} style={sideInput} /></SideField>
            <SideField label="Due Date"><input type="date" value={card.due_date || ""} onChange={e => updateCard(card.id, { due_date: e.target.value || null })} style={sideInput} /></SideField>
            <SideField label="Est. Hours"><input type="number" value={card.estimated_hours || ""} onChange={e => updateCard(card.id, { estimated_hours: parseFloat(e.target.value) || null })} style={sideInput} placeholder="0" /></SideField>
            {fieldDefs.map(fd => { const val = fieldValues.find(v => v.card_id === card.id && v.field_def_id === fd.id); return (<SideField key={fd.id} label={fd.name}><FieldCell def={fd} value={val} onChange={(v) => updateFieldValue(card.id, fd.id, v)} teamMembers={teamMembers} /></SideField>); })}
            <SideField label="Labels">
              {(() => {
                // Standard Trello-style label colors. Toggle on/off by clicking.
                // Stored in card.labels as { color, name? } objects. Bar-style labels have no name.
                const LABEL_COLORS = [
                  { name: "Green",  value: "#2E8B57" },
                  { name: "Yellow", value: "#F59E0B" },
                  { name: "Orange", value: "#D4763A" },
                  { name: "Red",    value: "#DC2626" },
                  { name: "Purple", value: "#6B5B95" },
                  { name: "Blue",   value: "#4059AD" },
                  { name: "Teal",   value: "#3D8B8B" },
                  { name: "Pink",   value: "#E91E63" },
                ];
                const currentLabels = card.labels || [];
                const isActive = (color) => currentLabels.some(l => l.color === color);
                const toggleLabel = (color, name) => {
                  const next = isActive(color)
                    ? currentLabels.filter(l => l.color !== color)
                    : [...currentLabels, { color, name: null }]; // bar-style: no name
                  updateCard(card.id, { labels: next });
                };
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {LABEL_COLORS.map(c => {
                      const active = isActive(c.value);
                      return (
                        <button
                          key={c.value}
                          onClick={() => toggleLabel(c.value, c.name)}
                          title={c.name + (active ? " (selected)" : "")}
                          style={{
                            width: 36, height: 16, borderRadius: 4,
                            background: c.value,
                            border: active ? "2px solid #FAFAFA" : "1px solid rgba(0,0,0,0.2)",
                            cursor: "pointer", padding: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            opacity: active ? 1 : 0.65,
                            transition: "all 0.12s",
                          }}
                          onMouseEnter={e => { if (!active) e.currentTarget.style.opacity = "0.9"; }}
                          onMouseLeave={e => { if (!active) e.currentTarget.style.opacity = "0.65"; }}
                        >
                          {active && <span style={{ color: "#fff", fontSize: 10, fontWeight: 700, lineHeight: 1 }}>✓</span>}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </SideField>
            <button onClick={() => toggleDone(card.id)} style={{ ...card.completed_at ? { ...gBtn, background: "rgba(46,139,87,0.09)", borderColor: "rgba(46,139,87,0.25)", color: "#2E8B57" } : gBtn, width: "100%", marginTop: 8, fontSize: 9, justifyContent: "center" }}>
              {card.completed_at ? "✓ Completed" : "Mark Done"}
            </button>
            <button onClick={() => archiveCard(card.id)} style={{ ...gBtn, width: "100%", marginTop: 6, fontSize: 9, justifyContent: "center" }}>Archive</button>
            <button onClick={() => setConfirmModal({ title: "Delete Card", message: 'Delete "' + card.title + '"?', onConfirm: () => deleteCard(card.id), danger: true })} style={{ ...gBtnDanger, width: "100%", marginTop: 6, fontSize: 9, justifyContent: "center" }}>Delete Card</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FIELD MANAGER ──
function FieldManager({ fieldDefs, addFieldDef, deleteFieldDef, onClose }) {
  const [name, setName] = useState(""); const [type, setType] = useState("text"); const [options, setOptions] = useState("");
  const [saving, setSaving] = useState(false);
  const TYPES = ["text","number","date","dropdown","person","checkbox","url","email","phone","rating","currency","percent","tags","color"];
  const handleAdd = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    const opts = type === "dropdown" ? options.split(",").map(o => o.trim()).filter(Boolean) : null;
    const ok = await addFieldDef(name.trim(), type, opts);
    setSaving(false);
    if (ok) {
      setName("");
      setOptions("");
      setType("text");
    }
  };
  return (<Modal onClose={onClose} width={460}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Custom Fields</span>
      <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.txt, fontSize: 14 }}>×</button>
    </div>
    {fieldDefs.map(fd => (<div key={fd.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
      <span style={{ flex: 1, fontSize: 12, color: T.txt, fontWeight: 400 }}>{fd.name}</span>
      <Badge color={T.blue}>{fd.field_type}</Badge>
      <button onClick={() => deleteFieldDef(fd.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", fontSize: 13 }}>&#10005;</button>
    </div>))}
    {fieldDefs.length === 0 && <div style={{ padding: 16, textAlign: "center", color: T.txt, fontSize: 12 }}>No custom fields yet</div>}
    <div style={{ marginTop: 14, padding: 12, background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 400, color: T.txt, marginBottom: 8 }}>Add Field</div>
      <div style={{ display: "flex", gap: 6 }}>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Field name" onKeyDown={e => { if (e.key === "Enter") handleAdd(); }} style={{ ...gInput, flex: 1, fontSize: 11 }} />
        <select value={type} onChange={e => setType(e.target.value)} style={gInput}>{TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select>
      </div>
      {type === "dropdown" && <input value={options} onChange={e => setOptions(e.target.value)} placeholder="Options (comma-separated)" style={{ ...gInput, width: "100%", marginTop: 6, fontSize: 11 }} />}
      <button onClick={handleAdd} disabled={saving || !name.trim()} style={{ ...gBtnPrimary, marginTop: 8, fontSize: 9, opacity: (saving || !name.trim()) ? 0.5 : 1, cursor: (saving || !name.trim()) ? "not-allowed" : "pointer" }}>{saving ? "Saving..." : "Add Field"}</button>
    </div>
  </Modal>);
}

// ── AUTOMATIONS PANEL ──
function AutomationsPanel({ automations, columns, teamMembers, boardId, orgId, userId, onClose, reload }) {
  const [trigger, setTrigger] = useState("card_moved"); const [action, setAction] = useState("send_notification");
  const [name, setName] = useState(""); const [triggerCol, setTriggerCol] = useState(""); const [actionCol, setActionCol] = useState("");
  const [actionUser, setActionUser] = useState(""); const [actionMsg, setActionMsg] = useState("");
  const TRIGGERS = [{ id: "card_moved", label: "Card moved to column" }, { id: "card_assigned", label: "Card assigned" }, { id: "subtasks_complete", label: "All subtasks complete" }];
  const ACTIONS = [{ id: "send_notification", label: "Send notification" }, { id: "move_card", label: "Move card to column" }, { id: "assign_card", label: "Assign to user" }, { id: "mark_complete", label: "Mark complete" }];
  const create = async () => {
    if (!name.trim()) return; const tc = {}; if (trigger === "card_moved" && triggerCol) tc.to_column_id = triggerCol;
    const ac = {}; if (action === "move_card" && actionCol) ac.to_column_id = actionCol; if (action === "assign_card" && actionUser) ac.user_id = actionUser;
    if (action === "send_notification") { if (actionUser) ac.user_id = actionUser; if (actionMsg) ac.message = actionMsg; }
    await supabase.from("board_automations").insert({ org_id: orgId, board_id: boardId, name: name.trim(), trigger_type: trigger, trigger_config: tc, action_type: action, action_config: ac, created_by: userId });
    setName(""); reload();
  };
  const toggle = async (id, en) => { await supabase.from("board_automations").update({ enabled: en }).eq("id", id); reload(); };
  const del = async (id) => { await supabase.from("board_automations").delete().eq("id", id); reload(); };
  return (<Modal onClose={onClose} width={520}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{ fontSize: 14, fontWeight: 400, color: T.txt }}>Automations</span>
      <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.txt, fontSize: 14 }}>×</button>
    </div>
    {automations.map(a => (<div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 0", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
      <button onClick={() => toggle(a.id, !a.enabled)} style={{ width: 20, height: 20, borderRadius: 8, border: "1.5px solid " + (a.enabled ? "#4059AD" : "rgba(217,219,221,0.12)"), background: a.enabled ? "#4059AD" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff", fontSize: 10 }}>{a.enabled ? "\u2713" : ""}</button>
      <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{a.name}</div><div style={{ fontSize: 10, color: T.txt, marginTop: 1 }}>When: {a.trigger_type} &rarr; Then: {a.action_type}</div></div>
      <button onClick={() => del(a.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", fontSize: 13 }}>&#10005;</button>
    </div>))}
    {automations.length === 0 && <div style={{ padding: 20, textAlign: "center", color: T.txt, fontSize: 12 }}>No automations yet</div>}
    <div style={{ marginTop: 14, padding: 12, background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 400, color: T.txt, marginBottom: 8 }}>New Automation</div>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Automation name" style={{ ...gInput, width: "100%", fontSize: 11, marginBottom: 8 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 400, color: T.txt, marginBottom: 3, textTransform: "uppercase", letterSpacing: ".06em" }}>When...</div>
          <select value={trigger} onChange={e => setTrigger(e.target.value)} style={{ ...gInput, width: "100%", fontSize: 11 }}>{TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
          {trigger === "card_moved" && <select value={triggerCol} onChange={e => setTriggerCol(e.target.value)} style={{ ...gInput, width: "100%", fontSize: 11, marginTop: 4 }}><option value="">Any column</option>{columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 400, color: T.txt, marginBottom: 3, textTransform: "uppercase", letterSpacing: ".06em" }}>Then...</div>
          <select value={action} onChange={e => setAction(e.target.value)} style={{ ...gInput, width: "100%", fontSize: 11 }}>{ACTIONS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}</select>
          {action === "move_card" && <select value={actionCol} onChange={e => setActionCol(e.target.value)} style={{ ...gInput, width: "100%", fontSize: 11, marginTop: 4 }}><option value="">Select column</option>{columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
          {(action === "assign_card" || action === "send_notification") && <select value={actionUser} onChange={e => setActionUser(e.target.value)} style={{ ...gInput, width: "100%", fontSize: 11, marginTop: 4 }}><option value="">{action === "send_notification" ? "Card assignee" : "Select user"}</option>{teamMembers.map(m => <option key={m._dbId} value={m._dbId}>{m.name}</option>)}</select>}
          {action === "send_notification" && <input value={actionMsg} onChange={e => setActionMsg(e.target.value)} placeholder="Message" style={{ ...gInput, width: "100%", fontSize: 11, marginTop: 4 }} />}
        </div>
      </div>
      <button onClick={create} style={{ ...gBtnPrimary, marginTop: 10, fontSize: 9 }}>Create Automation</button>
    </div>
  </Modal>);
}
